import { Injectable } from "@angular/core";
import { Tags } from "exifreader";
import { defer, firstValueFrom, Observable } from "rxjs";
import { BookMarkOption } from "../../_objects/inspection-image";
import { MarkItem } from "../../_objects/mark";
import { TagItems, TagMatch } from "../../_objects/tag-items";
import { ExifReaderService } from "../exif-reader.service";
import { ImageCreationMapper } from "../image-upload/image-creation.mapper";
import { MarkOfflineService } from "./mark-offline.service";
import { MerryImageMetadata } from "./merry-image-metadata";
import { TagsOfflineService } from "./tags-offline.service";
import { TitleOfflineService } from "./title-offline.service";

/** What a Merry capture carries for ViSOL, already matched against the ViSOL settings. */
export interface MerryUploadAttributes {
  /** Mark to give the file through its name suffix: the enabled ViSOL mark of the first Merry tag that is one (MerryImageMetadata.mapTags). */
  mark: MarkItem | null;
  /** ViSOL title of the first Merry tag that is one. */
  title: string | null;
  /** ViSOL tags for Merry's other tags (neither a mark nor a title), added to the tag list when it has no tag for them and room (see applyTags). */
  tags: string[];
  /** Merry's other tags the tag list has no tag for, kept with the image for the edit dialog. */
  unmatchedTags: string[];
  comment: string | null;
  bookmarks: BookMarkOption[];
}

/** The Merry XMP properties used at upload, as the text Merry wrote. */
interface MerryXmp {
  model?: string;
  tags?: string;
  comments?: string;
  bookmarks?: string;
}

/** ExifTool writes the XMP of an MP4 in a top-level "uuid" box with this id (or in moov/udta/XMP_ for QuickTime files). */
const XMP_BOX_UUID = "be7acfcb97a942e89c71999491e3afac";

/**
 * Reads, at upload, the tags, comments and bookmarks a Merry capture keeps in XMP, and maps them to the ViSOL mark,
 * title, tags, comment and bookmarks. The mark has to be known before the file is named, since a ViSOL mark is the
 * "_<code>" suffix of the file name.
 */
@Injectable({ providedIn: "root" })
export class MerryUploadService {
  /** Tag list reads and writes run one at a time: several files can be uploading at once. */
  private tagsQueue: Promise<unknown> = Promise.resolve();

  constructor(
    private exifReaderService: ExifReaderService,
    private markOfflineService: MarkOfflineService,
    private titleOfflineService: TitleOfflineService,
    private tagsOfflineService: TagsOfflineService
  ) {}

  /** Null for any file that is not a Merry capture, or that cannot be read: the upload then goes on as usual. */
  read(file: File): Observable<MerryUploadAttributes | null> {
    return defer(() => this.readAttributes(file));
  }

  private async readAttributes(file: File): Promise<MerryUploadAttributes | null> {
    try {
      const xmp = await this.readXmp(file);
      if (!xmp) {
        return null;
      }

      const [marks, titles] = await Promise.all([
        firstValueFrom(this.markOfflineService.getMark()),
        firstValueFrom(this.titleOfflineService.getTitles())
      ]);
      const mapping = MerryImageMetadata.mapTags(xmp.tags, marks?.items ?? [], titles?.items ?? []);
      const { tags, unmatchedTags } = await this.applyTags(mapping.tagLabels);
      return {
        mark: mapping.mark,
        title: mapping.title,
        tags,
        unmatchedTags,
        comment: MerryImageMetadata.toComment(xmp.comments),
        bookmarks: ImageCreationMapper.extractFromMerryBookmarks(xmp.bookmarks)
      };
    } catch (error) {
      console.error("Failed to read the Merry XMP metadata:", error);
      return null;
    }
  }

  /**
   * The ViSOL tags for Merry's tag labels: a label the tag list has no tag for is added to the list while it fits (TagItems
   * rules), else it stays unmatched. If the list cannot be read or saved, every label stays unmatched so none is lost.
   */
  private applyTags(labels: string[]): Promise<TagMatch> {
    if (!labels.length) {
      return Promise.resolve({ tags: [], unmatchedTags: [] });
    }

    const run = this.tagsQueue.then(async (): Promise<TagMatch> => {
      try {
        const existing = await firstValueFrom(this.tagsOfflineService.getTags());
        const items = TagItems.add(existing.items, labels);
        if (items.length !== existing.items.length) {
          await firstValueFrom(this.tagsOfflineService.upsertTags({ items, schemaVersion: existing.schemaVersion || "1" }));
        }
        return TagItems.match(items, labels);
      } catch (error) {
        console.error("Failed to add the Merry tags to the tag list:", error);
        return { tags: [], unmatchedTags: labels };
      }
    });
    this.tagsQueue = run;
    return run;
  }

  private async readXmp(file: File): Promise<MerryXmp | null> {
    if (/\.(mp4|mov)$/i.test(file.name)) {
      const packet = await readMp4XmpPacket(file);
      const xmp = packet ? parseXmpPacket(packet) : null;
      // An MP4 has no EXIF model: a Merry recording is recognized by the Merry properties in its XMP.
      const isMerry =
        !!xmp && (MerryImageMetadata.isMerryModel(xmp.model) || [xmp.tags, xmp.comments, xmp.bookmarks].some((value) => value !== undefined));
      return isMerry ? xmp : null;
    }

    if (!file.type.startsWith("image/")) {
      return null;
    }
    const tags = await this.exifReaderService.getTags(file);
    const model = tagText(tags, "Model");
    if (!MerryImageMetadata.isMerryModel(model)) {
      return null;
    }
    return { model, tags: tagText(tags, "tags"), comments: tagText(tags, "comments"), bookmarks: tagText(tags, "bookmarks") };
  }
}

function tagText(tags: Tags, name: string): string | undefined {
  const description = (tags?.[name] as { description?: unknown } | undefined)?.description;
  return typeof description === "string" ? description : undefined;
}

/** Reads each XMP property by local name, whether written as an element or as an rdf:Description attribute. */
function parseXmpPacket(packet: string): MerryXmp {
  const elements = Array.from(new DOMParser().parseFromString(packet, "application/xml").getElementsByTagName("*"));
  const read = (name: string): string | undefined => {
    const localName = name.toLowerCase();
    for (const element of elements) {
      if (element.localName.toLowerCase() === localName) {
        return element.textContent ?? undefined;
      }
      const attribute = Array.from(element.attributes).find((candidate) => candidate.localName.toLowerCase() === localName);
      if (attribute) {
        return attribute.value;
      }
    }
    return undefined;
  };
  return { model: read("Model"), tags: read("tags"), comments: read("comments"), bookmarks: read("bookmarks") };
}

interface Mp4Box {
  type: string;
  contentStart: number;
  end: number;
}

/** The XMP packet of an MP4 or MOV, reading only box headers until the XMP box is found. */
async function readMp4XmpPacket(file: Blob): Promise<string | null> {
  const readText = async (start: number, end: number) => new TextDecoder().decode(await file.slice(start, end).arrayBuffer());

  for (const box of await readBoxes(file, 0, file.size)) {
    if (box.type === "uuid") {
      const uuid = new Uint8Array(await file.slice(box.contentStart, box.contentStart + 16).arrayBuffer());
      if (Array.from(uuid, (byte) => byte.toString(16).padStart(2, "0")).join("") === XMP_BOX_UUID) {
        return readText(box.contentStart + 16, box.end);
      }
    } else if (box.type === "moov") {
      const userData = (await readBoxes(file, box.contentStart, box.end)).find((child) => child.type === "udta");
      const xmp = userData ? (await readBoxes(file, userData.contentStart, userData.end)).find((child) => child.type === "XMP_") : null;
      if (xmp) {
        return readText(xmp.contentStart, xmp.end);
      }
    }
  }
  return null;
}

async function readBoxes(file: Blob, start: number, end: number): Promise<Mp4Box[]> {
  const boxes: Mp4Box[] = [];
  for (let offset = start; offset + 8 <= end; ) {
    const header = new DataView(await file.slice(offset, Math.min(offset + 16, end)).arrayBuffer());
    const type = String.fromCharCode(header.getUint8(4), header.getUint8(5), header.getUint8(6), header.getUint8(7));
    let size = header.getUint32(0);
    let headerSize = 8;
    if (size === 1 && header.byteLength >= 16) {
      size = Number(header.getBigUint64(8));
      headerSize = 16;
    } else if (size === 0) {
      size = end - offset;
    }
    if (size < headerSize || offset + size > end) {
      break;
    }
    boxes.push({ type, contentStart: offset + headerSize, end: offset + size });
    offset += size;
  }
  return boxes;
}
